import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type { AgencyUser, LeadListItem, Role } from '../types';
import {
  COVERAGE_LABELS,
  STAGE_FILTERS,
  formatDateTime,
  formatPhone,
  formatPrice,
} from '../leadLabels';
import { LeadDetail } from './LeadDetail';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { SortableHeader, type SortDirection } from '../components/SortableHeader';
import { AddLeadModal } from '../components/AddLeadModal';
import { ImportLeadsModal } from '../components/ImportLeadsModal';

const PAGE_SIZE = 50;

/** Full "vehicle" label for a lead. */
function vehicleOf(l: LeadListItem): string {
  return [l.car_manufacturer, l.car_model, l.car_year].filter(Boolean).join(' ');
}

/**
 * Leads workspace following Noya's "policies per employee" pattern: title +
 * actions, a search bar, a collapsible multi-select filter panel, a sortable
 * grid-based table with expandable rows, and top/bottom pagination.
 */
export function Leads({
  tenantId,
  role,
  email,
}: {
  tenantId?: string;
  role: Role;
  email?: string;
}) {
  // AgencyAdmin / SystemAdmin act as the "manager" who distributes leads.
  const isManager = role === 'AgencyAdmin' || role === 'SystemAdmin';

  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  // Agency users (for the assignee picker + filter). Managers only.
  const [users, setUsers] = useState<AgencyUser[]>([]);

  // Bulk selection + assignment (manager distributing leads to reps).
  const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set());
  const [assignTarget, setAssignTarget] = useState<string>('');
  const [assigning, setAssigning] = useState(false);

  // Search (server-side, debounced).
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Filters (client-side, adapted to real lead fields).
  const [showFilters, setShowFilters] = useState(true);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterCoverage, setFilterCoverage] = useState<string[]>([]);
  const [filterManufacturers, setFilterManufacturers] = useState<string[]>([]);
  // A representative sees only their own leads by default, but can clear the
  // filter to view all of the agency's leads.
  const [filterAgents, setFilterAgents] = useState<string[]>(() =>
    role === 'Agent' && email ? [email] : [],
  );
  const [updatedFrom, setUpdatedFrom] = useState('');
  const [updatedTo, setUpdatedTo] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');

  // Sort + pagination + expansion.
  const [sortKey, setSortKey] = useState<string | null>('updated_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLDivElement>(null);

  // Manual-create / Excel-import dialogs.
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    api
      .listLeads({ q: debouncedQ.trim() || undefined }, tenantId)
      .then(setLeads)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [debouncedQ, tenantId]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  // Load the agency's users once (managers only) for the assignee picker/filter.
  useEffect(() => {
    if (!isManager) return;
    api
      .listUsers(tenantId)
      .then(setUsers)
      .catch(() => undefined);
  }, [isManager, tenantId]);

  // Debounce the search box (300ms) and reset to the first page.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  // Any filter/sort change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [filterStatuses, filterCoverage, filterManufacturers, filterAgents, updatedFrom, updatedTo, createdFrom, createdTo, sortKey, sortDir]);

  // Distinct option lists derived from the loaded leads.
  const manufacturerOptions = useMemo(() => {
    const s = new Set<string>();
    leads.forEach((l) => l.car_manufacturer && s.add(l.car_manufacturer));
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'he')).map((v) => ({ value: v, label: v }));
  }, [leads]);

  // Assignee options: the agency's users (from Cognito) merged with any
  // assigned_to values already present on leads (e.g. imported free-text names).
  const agentOptions = useMemo(() => {
    const labels = new Map<string, string>();
    users.forEach((u) => labels.set(u.email, u.name ? `${u.name} (${u.email})` : u.email));
    leads.forEach((l) => {
      if (l.assigned_to && !labels.has(l.assigned_to)) labels.set(l.assigned_to, l.assigned_to);
    });
    return Array.from(labels.entries())
      .sort((a, b) => a[1].localeCompare(b[1], 'he'))
      .map(([value, label]) => ({ value, label }));
  }, [leads, users]);

  const statusOptions = STAGE_FILTERS.map((s) => ({ value: s.key, label: s.label }));
  const coverageOptions = Object.entries(COVERAGE_LABELS).map(([value, label]) => ({ value, label }));

  // Apply client-side filters.
  const filtered = useMemo(() => {
    const uFrom = updatedFrom ? new Date(updatedFrom).getTime() : undefined;
    const uTo = updatedTo ? new Date(updatedTo).getTime() + 86_399_999 : undefined;
    const cFrom = createdFrom ? new Date(createdFrom).getTime() : undefined;
    const cTo = createdTo ? new Date(createdTo).getTime() + 86_399_999 : undefined;
    return leads.filter((l) => {
      if (filterStatuses.length && !filterStatuses.includes(l.stage.key)) return false;
      if (filterCoverage.length && !(l.coverage_type && filterCoverage.includes(l.coverage_type))) return false;
      if (filterManufacturers.length && !(l.car_manufacturer && filterManufacturers.includes(l.car_manufacturer))) return false;
      if (filterAgents.length && !(l.assigned_to && filterAgents.includes(l.assigned_to))) return false;
      const u = l.updated_at ? new Date(l.updated_at).getTime() : undefined;
      if (uFrom !== undefined && (u === undefined || u < uFrom)) return false;
      if (uTo !== undefined && (u === undefined || u > uTo)) return false;
      const c = l.created_at ? new Date(l.created_at).getTime() : undefined;
      if (cFrom !== undefined && (c === undefined || c < cFrom)) return false;
      if (cTo !== undefined && (c === undefined || c > cTo)) return false;
      return true;
    });
  }, [leads, filterStatuses, filterCoverage, filterManufacturers, filterAgents, updatedFrom, updatedTo, createdFrom, createdTo]);

  // Sort (client-side).
  const sorted = useMemo(() => {
    if (!sortKey || !sortDir) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (l: LeadListItem): string | number => {
      switch (sortKey) {
        case 'customer': return (l.customer_name || l.phone).toLowerCase();
        case 'status': return l.stage.label;
        case 'vehicle': return vehicleOf(l).toLowerCase();
        case 'coverage': return l.coverage_type ? COVERAGE_LABELS[l.coverage_type] : '';
        case 'last_offer_amount': return l.last_quote?.annualPremium ?? -1;
        case 'created_at': return l.created_at ? new Date(l.created_at).getTime() : 0;
        case 'updated_at': return l.updated_at ? new Date(l.updated_at).getTime() : 0;
        default: return '';
      }
    };
    return [...filtered].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), 'he') * dir;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = sorted.slice(pageStart, pageStart + PAGE_SIZE);

  function handleSort(key: string) {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') {
        setSortDir(null);
        setSortKey(null);
      } else setSortDir('asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function goToPage(p: number) {
    setPage(p);
    requestAnimationFrame(() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  function toggleExpand(phone: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  }

  function toggleSelect(phone: string) {
    setSelectedPhones((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  }

  const pageAllSelected =
    pageItems.length > 0 && pageItems.every((l) => selectedPhones.has(l.phone));

  function toggleSelectPage() {
    setSelectedPhones((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) pageItems.forEach((l) => next.delete(l.phone));
      else pageItems.forEach((l) => next.add(l.phone));
      return next;
    });
  }

  function clearSelection() {
    setSelectedPhones(new Set());
    setAssignTarget('');
  }

  async function assignSelected() {
    if (selectedPhones.size === 0) return;
    setAssigning(true);
    try {
      // assignTarget '' clears the assignment; the API treats empty as unassign.
      await api.assignLeads(Array.from(selectedPhones), assignTarget, tenantId);
      clearSelection();
      refresh();
    } catch {
      // Surface nothing intrusive; the list simply won't change on failure.
    } finally {
      setAssigning(false);
    }
  }

  const hasActiveFilters =
    filterStatuses.length > 0 || filterCoverage.length > 0 || filterManufacturers.length > 0 ||
    filterAgents.length > 0 || updatedFrom !== '' || updatedTo !== '' || createdFrom !== '' || createdTo !== '';

  const activeFilterCount = [
    filterStatuses.length > 0, filterCoverage.length > 0, filterManufacturers.length > 0,
    filterAgents.length > 0, updatedFrom !== '', updatedTo !== '', createdFrom !== '', createdTo !== '',
  ].filter(Boolean).length;

  function clearAllFilters() {
    setFilterStatuses([]);
    setFilterCoverage([]);
    setFilterManufacturers([]);
    setFilterAgents([]);
    setUpdatedFrom('');
    setUpdatedTo('');
    setCreatedFrom('');
    setCreatedTo('');
  }

  function exportCsv() {
    const headers = ['שם לקוח', 'טלפון', 'סטטוס מערכת', 'יצרן', 'דגם', 'שנה', 'כיסוי', 'הצעה אחרונה', 'נציג', 'עודכן', 'נוצר'];
    const rows = sorted.map((l) => [
      l.customer_name || '',
      l.phone,
      l.stage.label,
      l.car_manufacturer || '',
      l.car_model || '',
      l.car_year ? String(l.car_year) : '',
      l.coverage_type ? COVERAGE_LABELS[l.coverage_type] : '',
      l.last_quote?.annualPremium ? String(l.last_quote.annualPremium) : '',
      l.assigned_to || '',
      l.updated_at || '',
      l.created_at || '',
    ]);
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (selected) {
    return (
      <LeadDetail
        phone={selected}
        tenantId={tenantId}
        role={role}
        onBack={() => {
          setSelected(null);
          refresh();
        }}
      />
    );
  }

  // Compact page-number list with ellipses.
  const pageNumbers: (number | 'gap')[] = [];
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1) pageNumbers.push(p);
    else if (pageNumbers[pageNumbers.length - 1] !== 'gap') pageNumbers.push('gap');
  }

  const Pagination = () =>
    totalPages > 1 ? (
      <div className="results-bar">
        <span>
          מציג {sorted.length === 0 ? 0 : pageStart + 1}-{Math.min(pageStart + PAGE_SIZE, sorted.length)} מתוך{' '}
          {sorted.length.toLocaleString('he-IL')}
        </span>
        <div className="spacer" />
        <div className="pager">
          <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
            הקודם
          </button>
          {pageNumbers.map((p, i) =>
            p === 'gap' ? (
              <span key={`gap-${i}`} className="ellipsis">…</span>
            ) : (
              <button key={p} className={p === currentPage ? 'active' : ''} onClick={() => goToPage(p)}>
                {p}
              </button>
            ),
          )}
          <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}>
            הבא
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
          </button>
        </div>
      </div>
    ) : (
      <div className="results-bar">
        <span>{sorted.length.toLocaleString('he-IL')} לידים</span>
      </div>
    );

  return (
    <div className="leads">
      <div className="leads-header">
        <div>
          <h1 className="page-title">לידים</h1>
          <p className="page-subtitle">ניהול כל הלידים במערכת</p>
        </div>
        <div className="leads-actions">
          <button className="btn" onClick={() => setShowAdd(true)}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
            הוספת ליד
          </button>
          <button className="btn outline" onClick={() => setShowImport(true)}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 14.9V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2.1M12 3v12M7 8l5-5 5 5" /></svg>
            ייבוא מאקסל
          </button>
          <button className="btn outline" onClick={exportCsv}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
            ייצוא לאקסל
          </button>
          <button className="btn" onClick={refresh} disabled={loading}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3L21 8M21 3v5h-5M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3L3 16M3 21v-5h5" /></svg>
            {loading ? 'מרענן…' : 'רענון'}
          </button>
        </div>
      </div>

      <div className="leads-card">
        <div className="leads-card-header">
          <div className="search-and-tools">
            <div className="search-box">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                className="leads-search"
                value={q}
                placeholder="חיפוש לפי טלפון, שם, ת&quot;ז, מספר רכב, רכב…"
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <div className="leads-actions">
              <button
                className={`btn ${showFilters ? '' : 'outline'}`}
                onClick={() => setShowFilters((s) => !s)}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></svg>
                פילטרים
                {hasActiveFilters && <span className="count-badge">{activeFilterCount}</span>}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="filters-panel">
              <div className="filters-line">
                <div className="filter-group">
                  <span className="flabel">סטטוס מערכת:</span>
                  <MultiSelectFilter options={statusOptions} selected={filterStatuses} onChange={setFilterStatuses} searchPlaceholder="חפש סטטוס מערכת…" />
                </div>
                <div className="filter-group">
                  <span className="flabel">כיסוי:</span>
                  <MultiSelectFilter options={coverageOptions} selected={filterCoverage} onChange={setFilterCoverage} searchPlaceholder="חפש כיסוי…" />
                </div>
                <div className="filter-group">
                  <span className="flabel">יצרן רכב:</span>
                  <MultiSelectFilter options={manufacturerOptions} selected={filterManufacturers} onChange={setFilterManufacturers} searchPlaceholder="חפש יצרן…" />
                </div>
                <div className="filter-group">
                  <span className="flabel">נציג מטפל:</span>
                  <MultiSelectFilter options={agentOptions} selected={filterAgents} onChange={setFilterAgents} searchPlaceholder="חפש נציג…" />
                </div>
              </div>

              <div className="filters-line dates">
                <div className="filter-group">
                  <svg className="cal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                  <span className="flabel">עודכן:</span>
                  <input className="date-input" type="date" value={updatedFrom} onChange={(e) => setUpdatedFrom(e.target.value)} />
                  <span className="date-sep">עד</span>
                  <input className="date-input" type="date" value={updatedTo} onChange={(e) => setUpdatedTo(e.target.value)} />
                </div>
                <div className="filter-group">
                  <svg className="cal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                  <span className="flabel">נוצר:</span>
                  <input className="date-input" type="date" value={createdFrom} onChange={(e) => setCreatedFrom(e.target.value)} />
                  <span className="date-sep">עד</span>
                  <input className="date-input" type="date" value={createdTo} onChange={(e) => setCreatedTo(e.target.value)} />
                </div>
                {hasActiveFilters && (
                  <button className="btn danger clear-all" onClick={clearAllFilters}>
                    נקה הכל
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="leads-card-body" ref={tableRef}>
          {isManager && selectedPhones.size > 0 && (
            <div className="bulk-assign-bar">
              <span className="bulk-count">{selectedPhones.size} לידים נבחרו</span>
              <div className="spacer" />
              <span className="flabel">שייך לנציג:</span>
              <select
                className="assign-select"
                value={assignTarget}
                onChange={(e) => setAssignTarget(e.target.value)}
              >
                <option value="">— ללא שיוך —</option>
                {agentOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button className="btn" onClick={assignSelected} disabled={assigning}>
                {assigning ? 'משייך…' : 'שייך נציג נבחר'}
              </button>
              <button className="btn outline" onClick={clearSelection} disabled={assigning}>
                בטל בחירה
              </button>
            </div>
          )}

          <Pagination />

          {pageItems.length === 0 ? (
            <div className="empty">
              {hasActiveFilters || debouncedQ ? 'לא נמצאו לידים התואמים את הפילטרים' : 'לא נמצאו לידים'}
            </div>
          ) : (
            <>
              <div className={`lead-grid-header${isManager ? ' with-select' : ''}`}>
                {isManager && (
                  <span className="select-cell">
                    <input
                      type="checkbox"
                      checked={pageAllSelected}
                      onChange={toggleSelectPage}
                      title="בחר את כל הלידים בעמוד"
                    />
                  </span>
                )}
                <SortableHeader label="שם מלא" sortKey="customer" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
                <SortableHeader label="סטטוס מערכת" sortKey="status" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
                <span className="sortable">טלפון נייד</span>
                <span className="sortable">ת&quot;ז</span>
                <span className="sortable">מספר רכב</span>
                <SortableHeader label="רכב" sortKey="vehicle" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
                <SortableHeader label="כיסוי" sortKey="coverage" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
                <SortableHeader label="הצעה אחרונה" sortKey="last_offer_amount" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
                <SortableHeader label="נוצר" sortKey="created_at" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
                <SortableHeader label="עודכן" sortKey="updated_at" currentSort={sortKey} currentDirection={sortDir} onSort={handleSort} />
                <span className="sortable">טיפול</span>
                <span />
              </div>

              <div className="lead-list">
                {pageItems.map((l) => (
                  <LeadRow
                    key={l.phone}
                    lead={l}
                    expanded={expanded.has(l.phone)}
                    onToggleExpand={() => toggleExpand(l.phone)}
                    onOpen={() => setSelected(l.phone)}
                    selectable={isManager}
                    selected={selectedPhones.has(l.phone)}
                    onToggleSelect={() => toggleSelect(l.phone)}
                  />
                ))}
              </div>
            </>
          )}

          <Pagination />
        </div>
      </div>

      {showAdd && (
        <AddLeadModal
          tenantId={tenantId}
          onClose={() => setShowAdd(false)}
          onCreated={(phone) => {
            setShowAdd(false);
            refresh();
            setSelected(phone);
          }}
        />
      )}

      {showImport && (
        <ImportLeadsModal
          tenantId={tenantId}
          onClose={() => setShowImport(false)}
          onImported={refresh}
        />
      )}
    </div>
  );
}

/** A single expandable lead row (desktop grid / mobile card via CSS). */
function LeadRow({
  lead,
  expanded,
  onToggleExpand,
  onOpen,
  selectable,
  selected,
  onToggleSelect,
}: {
  lead: LeadListItem;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpen: () => void;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const vehicle = vehicleOf(lead);
  const coverage = lead.coverage_type ? COVERAGE_LABELS[lead.coverage_type] : '—';

  return (
    <div className={`lead-row${expanded ? ' expanded' : ''}`}>
      <div className={`lead-row-main${selectable ? ' with-select' : ''}`} onClick={onToggleExpand}>
        {selectable && (
          <span className="select-cell" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              title="בחירת ליד לשיוך"
            />
          </span>
        )}
        {/* שם מלא */}
        <div className="lead-cell">
          <span className="cell-microlabel">שם מלא</span>
          <div className="cell-value">
            <button
              type="button"
              className="link-phone"
              title="פתיחת כרטיס ליד"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              {lead.customer_name || '—'}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
            </button>
          </div>
        </div>

        {/* סטטוס מערכת — עמודה ראשית ובולטת */}
        <div className="lead-cell lead-cell-status">
          <span className="cell-microlabel">סטטוס מערכת</span>
          <div className="cell-value">
            <span className={`stage-badge stage-badge-primary heat-${lead.stage.heat}`}>
              {lead.stage.label}
            </span>
          </div>
        </div>

        {/* טלפון נייד */}
        <div className="lead-cell">
          <span className="cell-microlabel">טלפון נייד</span>
          <div className="cell-value truncate">{formatPhone(lead.phone)}</div>
        </div>

        {/* ת"ז */}
        <div className="lead-cell">
          <span className="cell-microlabel">ת&quot;ז</span>
          <div className="cell-value truncate">{lead.national_id || '—'}</div>
        </div>

        {/* מספר רכב */}
        <div className="lead-cell">
          <span className="cell-microlabel">מספר רכב</span>
          <div className="cell-value truncate">{lead.car_number || '—'}</div>
        </div>

        {/* רכב */}
        <div className="lead-cell">
          <span className="cell-microlabel">רכב</span>
          <div className="cell-value truncate strong">{vehicle || '—'}</div>
        </div>

        {/* כיסוי */}
        <div className="lead-cell">
          <span className="cell-microlabel">כיסוי</span>
          <div className="cell-value"><span className="chip">{coverage}</span></div>
        </div>

        {/* הצעה אחרונה */}
        <div className="lead-cell">
          <span className="cell-microlabel">הצעה אחרונה</span>
          <div className="cell-value strong currency">{formatPrice(lead.last_quote?.annualPremium, lead.last_quote?.currency)}</div>
        </div>

        {/* נוצר */}
        <div className="lead-cell">
          <span className="cell-microlabel">נוצר</span>
          <div className="cell-value"><span className="chip">{formatDateTime(lead.created_at)}</span></div>
        </div>

        {/* עודכן */}
        <div className="lead-cell">
          <span className="cell-microlabel">עודכן</span>
          <div className="cell-value"><span className="chip">{formatDateTime(lead.updated_at)}</span></div>
        </div>

        {/* טיפול */}
        <div className="lead-cell">
          <span className="cell-microlabel">טיפול</span>
          <div className="cell-value handling-tag">
            {lead.conversation?.is_human_handling && <span className="stage-badge heat-closing">נציג</span>}
            {lead.conversation?.is_blocked && <span className="stage-badge heat-lost">חסום</span>}
            {!lead.conversation?.is_human_handling && !lead.conversation?.is_blocked && (
              <span className="stage-badge heat-cold">בוט</span>
            )}
          </div>
        </div>

        {/* Chevron */}
        <div className="chevron-cell">
          {expanded ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
          )}
        </div>
      </div>

      {expanded && (
        <div className="lead-expanded">
          <div className="lead-expanded-grid">
            <div className="lead-expanded-field">
              <span className="cell-microlabel">שם מלא</span>
              <div className="cell-value">{lead.customer_name || '—'}</div>
            </div>
            <div className="lead-expanded-field">
              <span className="cell-microlabel">נציג מטפל</span>
              <div className="cell-value">{lead.assigned_to || '—'}</div>
            </div>
            <div className="lead-expanded-field">
              <span className="cell-microlabel">ת.ז.</span>
              <div className="cell-value">{lead.national_id || '—'}</div>
            </div>
            <div className="lead-expanded-field">
              <span className="cell-microlabel">עיר</span>
              <div className="cell-value">{lead.main_driver_city || '—'}</div>
            </div>
            <div className="lead-expanded-field">
              <span className="cell-microlabel">נוצר</span>
              <div className="cell-value">{formatDateTime(lead.created_at)}</div>
            </div>
          </div>
          <div className="lead-expanded-actions">
            <button
              className="btn"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              פתיחת כרטיס ליד
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
